<template>
  <form @submit.prevent="saveSensor" ref="editComputedSensorForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="editComputedSensorModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ action === 'edit' ? $t('editComputedSensorModal.titleEdit') : $t('editComputedSensorModal.titleAdd') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>
              <div v-if="parentFacility" class="form-group">
                <label>{{ $t('editComputedSensorModal.facilityLabel') }}</label>
                <div class="blue-text">{{ parentFacility.name }}</div>
              </div>
              <div class="form-group">
                <label for="computedSensorNameInput">{{ $t('editComputedSensorModal.nameInput') }}</label>
                <input type="text" class="form-control" :class="{ 'is-invalid': v$.sensor.name.$dirty && v$.sensor.name.$error }" id="computedSensorNameInput" v-model.trim="sensor.name" :placeholder="$t('editComputedSensorModal.namePlaceholder')" maxlength="50" />
                <div class="invalid-feedback" style="display: block;" v-if="v$.sensor.name.$dirty && v$.sensor.name.$error">
                  <div class="error" v-if="v$.sensor.name.required.$invalid">{{ $t('editComputedSensorModal.nameRequired') }}</div>
                </div>
              </div>
              <div class="form-group">
                <label for="computedSensorTypeSelect">{{ $t('editComputedSensorModal.typeInput') }}</label>
                <select class="form-control" id="computedSensorTypeSelect" v-model="sensor.type" @change="typeChanged">
                  <option v-for="t in allowedTypes" :value="t" :key="t">
                    {{ sensorTypeLabel(t) }}
                  </option>
                </select>
              </div>
              <div class="form-group pair-row">
                <div class="pair-field">
                  <label for="computedSensorSystempartSelect">{{ $t('editComputedSensorModal.outputSystempartInput') }}</label>
                  <select class="form-control" id="computedSensorSystempartSelect" v-model.number="sensor.systempart">
                    <option v-for="sp in systemParts" :value="sp" :key="sp">{{ sp }} – {{ $t('systemPart.' + sp) }}</option>
                  </select>
                </div>
                <div class="pair-field">
                  <label for="computedSensorValuetypeSelect">{{ $t('editComputedSensorModal.valuetypeInput') }}</label>
                  <select class="form-control" id="computedSensorValuetypeSelect" v-model.number="sensor.valuetype">
                    <option v-for="vt in valueTypes" :value="vt" :key="vt">{{ vt }} – {{ $t('valueType.' + vt) }}<span v-if="$t('physicalUnit.' + vt)"> [{{ $t('physicalUnit.' + vt) }}]</span></option>
                  </select>
                </div>
              </div>
              <div class="small text-muted pair-hint">{{ $t('editComputedSensorModal.pairHint') }}</div>
              <div class="form-group">
                <label>{{ $t('editComputedSensorModal.membersLabel') }}</label>
                <div class="small text-muted">{{ $t('editComputedSensorModal.sameTypeHint') }}</div>
                <div v-for="(row, index) in memberRows" :key="index" class="member-row">
                  <button type="button" class="btn btn-outline-secondary btn-sm sign-button" :title="row.sign === 1 ? $t('editComputedSensorModal.signPlus') : $t('editComputedSensorModal.signMinus')" @click="toggleSign(row)">
                    {{ row.sign === 1 ? '+' : '−' }}
                  </button>
                  <select class="form-control member-select" v-model="row.id">
                    <option value="">{{ $t('editComputedSensorModal.memberPlaceholder') }}</option>
                    <option v-for="candidate in candidatesForRow(row)" :value="candidate.id" :key="candidate.id">
                      {{ candidate.name }}<span v-if="candidate.enabled === false"> ({{ $t('editComputedSensorModal.memberDisabled') }})</span>
                    </option>
                  </select>
                  <select class="form-control member-sp-select" v-model="row.systempart" :title="$t('editComputedSensorModal.memberSystempartTitle')">
                    <option value="">{{ $t('editComputedSensorModal.memberSystempartDefault') }}</option>
                    <option v-for="sp in systemParts" :value="sp" :key="sp">{{ sp }} – {{ $t('systemPart.' + sp) }}</option>
                  </select>
                  <button type="button" class="btn btn-outline-secondary btn-sm remove-button" :title="$t('editComputedSensorModal.removeRow')" @click="removeRow(index)">
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm mt-2" @click="addRow">
                  <font-awesome-icon icon="plus" /> &nbsp; {{ $t('editComputedSensorModal.addMemberRow') }}
                </button>
                <div class="invalid-feedback" style="display: block;" v-if="showMinMembersError">
                  {{ $t('editComputedSensorModal.minMembersError') }}
                </div>
                <div class="invalid-feedback" style="display: block;" v-if="showDuplicateError">
                  {{ $t('editComputedSensorModal.duplicateMembersError') }}
                </div>
              </div>
              <div class="form-group">
                <label for="computedSensorStatusinfoInput">{{ $t('editComputedSensorModal.statusinfoInput') }}</label>
                <input type="text" class="form-control" id="computedSensorStatusinfoInput" v-model.trim="sensor.statusinfo" maxlength="255" />
              </div>
              <div class="form-group">
                <label for="computedSensorExternalidInput">{{ $t('editComputedSensorModal.externalidInput') }}</label>
                <input type="text" class="form-control" id="computedSensorExternalidInput" v-model.trim="sensor.externalid" maxlength="64" />
              </div>
              <div class="form-group">
                <label for="computedSensorMainInput">{{ $t('editComputedSensorModal.mainInput') }}</label>
                &nbsp;&nbsp;<input type="checkbox" id="computedSensorMainInput" v-model="sensor.main">
              </div>
              <div class="form-group">
                <label for="computedSensorEnabledInput">{{ $t('editComputedSensorModal.enabledInput') }}</label>
                &nbsp;&nbsp;<input type="checkbox" id="computedSensorEnabledInput" v-model="sensor.enabled">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary">{{ action === 'edit' ? $t('editComputedSensorModal.buttonSave') : $t('editComputedSensorModal.buttonAdd') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('editComputedSensorModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import { required, maxLength } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import cdcsensorService from '@/services/cdcsensors'
import { isComputedSensor, parseMembers, buildMembersPayload, defaultPairForType, computedPair } from '@/utils/computed-sensors'

// Code ranges of CDCValue.SystemPart (0..32) and CDCValue.ValueType (1..36;
// 0 = unknown is not selectable), labelled via the systemPart./valueType.
// i18n maps.
const SYSTEM_PARTS = Array.from({ length: 33 }, (_, i) => i)
const VALUE_TYPES = Array.from({ length: 36 }, (_, i) => i + 1)

export default {
  setup () { return { v$: useVuelidate() } },
  name: 'EditComputedSensorModal',
  props: {
    parentFacility: {
      type: Object,
      required: true
    },
    // Full sensor object (admin shape incl. json) when editing, null for add.
    selectedSensor: {
      type: Object,
      default: null
    },
    // All sensors of the facility the picker may choose from (computed
    // sensors and the edited sensor itself are filtered out here).
    candidateSensors: {
      type: Array,
      default: () => []
    },
    action: {
      type: String,
      default: 'add'
    },
    modalOpened: {
      type: String,
      required: true
    }
  },
  emits: ['modalClosed'],
  data () {
    return {
      sensor: this.emptySensor(),
      memberRows: [],
      allowedTypes: [0, 1, 2, 3, 4],
      systemParts: SYSTEM_PARTS,
      valueTypes: VALUE_TYPES,
      showMinMembersError: false,
      showDuplicateError: false,
      errorMessage: '',
      anyChanges: false
    }
  },
  validations: {
    sensor: {
      name: {
        required,
        maxLength: maxLength(50)
      }
    }
  },
  watch: {
    modalOpened () {
      this.resetForm()
      this.$nextTick(() => {
        document.getElementById('computedSensorNameInput')?.focus()
      })
    }
  },
  methods: {
    emptySensor () {
      const pair = defaultPairForType(0)
      return {
        name: '',
        type: 0,
        systempart: pair.systempart,
        valuetype: pair.valuetype,
        statusinfo: '',
        externalid: '',
        main: false,
        enabled: true
      }
    },
    resetForm () {
      this.errorMessage = ''
      this.anyChanges = false
      this.showMinMembersError = false
      this.showDuplicateError = false
      if (this.action === 'edit' && this.selectedSensor) {
        const pair = computedPair(this.selectedSensor)
        this.sensor = {
          name: this.selectedSensor.name || '',
          type: this.selectedSensor.type != null ? this.selectedSensor.type : 0,
          systempart: pair.systempart,
          valuetype: pair.valuetype,
          statusinfo: this.selectedSensor.statusinfo || this.selectedSensor.meterinfo || '',
          externalid: this.selectedSensor.externalid || '',
          main: this.selectedSensor.main === true,
          enabled: this.selectedSensor.enabled !== false
        }
        const members = parseMembers(this.selectedSensor.json) || []
        this.memberRows = members.map(member => ({ id: member.id, sign: member.sign, systempart: member.systempart != null ? member.systempart : '' }))
      } else {
        this.sensor = this.emptySensor()
        this.memberRows = []
      }
      while (this.memberRows.length < 2) {
        this.memberRows.push({ id: '', sign: this.memberRows.length === 0 ? 1 : -1, systempart: '' })
      }
      this.v$.$reset()
    },
    // Candidates of the selected type, no computed sensors, not the edited
    // sensor itself. The same sensor may be picked in several rows with
    // different systemparts (L1 + L2 + L3) — duplicates are checked on save.
    candidatesForRow (row) {
      const selfId = this.selectedSensor ? this.selectedSensor.id : null
      return this.candidateSensors.filter(candidate =>
        candidate.type === this.sensor.type &&
        !isComputedSensor(candidate) &&
        candidate.id !== selfId
      )
    },
    typeChanged () {
      // A new type brings its default reading (the user can still override)
      // and members must match the sensor type — clear rows that no longer fit.
      const pair = defaultPairForType(this.sensor.type)
      this.sensor.systempart = pair.systempart
      this.sensor.valuetype = pair.valuetype
      for (const row of this.memberRows) {
        if (row.id === '') continue
        const candidate = this.candidateSensors.find(c => Number(c.id) === Number(row.id))
        if (!candidate || candidate.type !== this.sensor.type) {
          row.id = ''
        }
      }
    },
    toggleSign (row) {
      row.sign = row.sign === 1 ? -1 : 1
    },
    addRow () {
      this.memberRows.push({ id: '', sign: 1, systempart: '' })
    },
    removeRow (index) {
      this.memberRows.splice(index, 1)
    },
    saveSensor () {
      this.v$.$touch()
      const members = buildMembersPayload(this.memberRows)
      this.showMinMembersError = members.length < 2
      const keys = members.map(member => member.id + '/' + (member.systempart != null ? member.systempart : ''))
      this.showDuplicateError = new Set(keys).size !== keys.length
      if (this.v$.$invalid || this.showMinMembersError || this.showDuplicateError || !this.parentFacility || !this.parentFacility.id) {
        return
      }
      const detail = {
        name: this.sensor.name,
        type: this.sensor.type,
        statusinfo: this.sensor.statusinfo,
        externalid: this.sensor.externalid,
        main: this.sensor.main,
        enabled: this.sensor.enabled,
        systempart: this.sensor.systempart,
        valuetype: this.sensor.valuetype,
        members
      }
      const request = (this.action === 'edit' && this.selectedSensor)
        ? cdcsensorService.updateComputedSensor(this.selectedSensor.id, detail)
        : cdcsensorService.addComputedSensor(this.parentFacility.id, detail)
      request.then(() => {
        this.anyChanges = true
        this.close()
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    onEsc () {
      this.close()
    },
    close () {
      this.$emit('modalClosed', this.anyChanges)
      Modal.getInstance(document.getElementById('editComputedSensorModal'))?.hide()
    },
    localizedBackendMessage (errorMessage) {
      return this.$t('backend.' + errorMessage)
    },
    sensorTypeLabel (type) {
      return type >= 0 ? this.$t('sensorType.' + type) : '? ' + type
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .modal-dialog {
    width: 520px;
  }
  .blue-text {
    color: #0069d9;
    font-weight: 700;
  }
  .pair-row {
    display: flex;
    gap: 8px;
    margin-bottom: 2px;

    .pair-field {
      flex: 1 1 50%;
      min-width: 0;
    }
  }
  .pair-hint {
    margin-bottom: 10px;
  }
  .member-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;

    .sign-button {
      width: 34px;
      flex: 0 0 auto;
      font-weight: 700;
    }
    .member-select {
      flex: 1 1 auto;
      min-width: 0;
    }
    .member-sp-select {
      flex: 0 0 140px;
    }
    .remove-button {
      flex: 0 0 auto;
    }
  }
}
</style>
